import React from "react";
import { labelFor } from "@/hooks/useReferences";

/** Compact money: $300K / €1.2M. Falls back to the ISO code when the browser can't map it. */
export function money(amount, currency = "USD") {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency, notation: "compact", maximumFractionDigits: 1,
    }).format(num);
  } catch {
    return `${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(num)} ${currency}`;
  }
}

/** "Aug 2026" from a YYYY-MM-DD field. */
export function monthYear(date) {
  if (!date) return "";
  const [y, m] = String(date).slice(0, 10).split("-").map(Number);
  if (!y || !m) return "";
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export function roundLabel(entry, references) {
  if (entry.round_type === "other") return entry.custom_round_type || "Other";
  return labelFor(references.funding_round_types, entry.round_type) || entry.round_type;
}

/** Progress only exists when a target was provided; oversubscribed rounds cap the bar at 100%. */
export function progressOf(entry) {
  const target = Number(entry.target_amount);
  const raised = Number(entry.amount_raised) || 0;
  if (!Number.isFinite(target) || target <= 0) return null;
  const pct = Math.round((raised / target) * 100);
  return { pct, width: Math.min(100, Math.max(0, pct)) };
}

export function StatusPill({ status, references }) {
  const label = labelFor(references.funding_statuses, status) || status;
  const tone = status === "open" ? "bg-teal-50 text-teal-700"
    : status === "closed" ? "bg-secondary text-muted-foreground"
      : "bg-amber-50 text-amber-700";
  return (
    <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-[640] uppercase tracking-wide ${tone}`}>
      {label}
    </span>
  );
}

export function AmountCell({ entry }) {
  const raised = money(entry.amount_raised, entry.currency);
  const progress = progressOf(entry);
  if (!progress) return <span>{raised} {entry.currency}</span>;
  return (
    <span className="inline-flex flex-col gap-1">
      <span>{raised} of {money(entry.target_amount, entry.currency)} {entry.currency}</span>
      <span className="flex items-center gap-2">
        <span className="h-1.5 w-20 overflow-hidden rounded-full bg-secondary">
          <span className="block h-full rounded-full bg-primary" style={{ width: `${progress.width}%` }} />
        </span>
        <span className="text-xs text-muted-foreground">{progress.pct}%</span>
      </span>
    </span>
  );
}

/** Newest-first funding rounds. Stacked rows at every width — the financial card lives in
 *  the slim right-hand column, where a multi-column table would truncate. */
export function FundingHistory({ entries, references }) {
  if (!entries.length) return null;
  return (
    <div className="mt-5" data-testid="org-funding-history">
      <h4 className="text-xs font-[640] uppercase tracking-wide text-muted-foreground">Funding history</h4>

      <div className="mt-3 space-y-3">
        {entries.map((e) => (
          <div key={e.id} className="rounded-xl border border-border p-3 text-sm" data-testid={`funding-row-${e.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-[640] uppercase tracking-wide text-foreground">{roundLabel(e, references)}</span>
              <StatusPill status={e.status} references={references} />
            </div>
            <div className="mt-2 font-[540] text-foreground"><AmountCell entry={e} /></div>
            {e.as_of_date && <div className="mt-1 text-xs text-muted-foreground">{e.status === "closed" ? "Closed" : "As of"} {monthYear(e.as_of_date)}</div>}
            {e.led_by && <div className="text-xs text-muted-foreground">Led by {e.led_by}</div>}
            {e.notes && <div className="mt-1 text-xs text-muted-foreground">{e.notes}</div>}
          </div>
        ))}
      </div>

    </div>
  );
}
